import React, { useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2,
  ClipboardCopy,
  Database,
  Globe,
  KeyRound,
  MapPin,
  RefreshCw,
  Send,
  Server,
  Shield,
  Wifi,
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import { API_URL, APP_BASE_URL, positionService, systemService, vehicleService } from '../services/api';
import { toast } from 'react-toastify';

const samplePayload = {
  imei: '860000000000001',
  latitude: -23.55052,
  longitude: -46.63331,
  speed: 42,
  heading: 90,
  battery: 88,
  ignition: true,
  motion: true,
  device_time: '2026-05-25T12:00:00.000Z',
};

const defaultSimulation = {
  vehicle_id: '',
  imei: '',
  latitude: '-23.55052',
  longitude: '-46.63331',
  speed: '35',
  heading: '90',
  battery: '85',
  ignition: true,
  motion: true,
  address: 'Posição simulada pelo painel',
};

const Settings = () => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [simulation, setSimulation] = useState(defaultSimulation);
  const [simulationLoading, setSimulationLoading] = useState(false);

  const ingestUrl = `${API_URL}/positions`;
  const curlExample = useMemo(() => (
    `curl -X POST "${ingestUrl}" \\\n` +
    `  -H "Content-Type: application/json" \\\n` +
    `  -H "x-tracker-token: SEU_TOKEN_DO_RASTREADOR" \\\n` +
    `  -d '${JSON.stringify(samplePayload)}'`
  ), [ingestUrl]);

  useEffect(() => {
    loadHealth();
    loadVehicles();
  }, []);

  useEffect(() => {
    if (!simulation.vehicle_id && vehicles.length > 0) {
      const firstVehicle = vehicles[0];
      setSimulation((current) => ({
        ...current,
        vehicle_id: firstVehicle.id,
        imei: firstVehicle.imei || '',
      }));
    }
  }, [simulation.vehicle_id, vehicles]);

  const loadHealth = async () => {
    setLoading(true);

    try {
      const response = await systemService.getHealth();
      setHealth(response.data);
    } catch (error) {
      setHealth({
        success: false,
        message: 'API indisponível',
        timestamp: new Date().toISOString(),
      });
    } finally {
      setLoading(false);
    }
  };

  const loadVehicles = async () => {
    try {
      const response = await vehicleService.getAll();
      setVehicles(response.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao carregar veículos para teste');
    }
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado`);
    } catch {
      toast.error('Não foi possível copiar');
    }
  };

  const handleSimulationChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (name === 'vehicle_id') {
      const vehicle = vehicles.find((item) => item.id === value);
      setSimulation((current) => ({
        ...current,
        vehicle_id: value,
        imei: vehicle?.imei || '',
      }));
      return;
    }

    setSimulation((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSimulationSubmit = async (event) => {
    event.preventDefault();
    setSimulationLoading(true);

    try {
      await positionService.simulate({
        ...simulation,
        device_time: new Date().toISOString(),
      });
      toast.success('Posição simulada enviada com sucesso');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao simular posição');
    } finally {
      setSimulationLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-dark-950">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Configurações</h1>
              <p className="text-dark-400">Status do sistema e integração com rastreadores.</p>
            </div>

            <button
              type="button"
              onClick={loadHealth}
              className="btn-secondary flex items-center justify-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Atualizar status
            </button>
          </div>

          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="card">
              <div className="mb-3 flex items-center gap-2 text-dark-400">
                <Server className="h-5 w-5" />
                <span className="text-sm">API</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`h-3 w-3 rounded-full ${health?.success ? 'bg-success-500' : 'bg-danger-500'}`} />
                <p className="text-xl font-bold text-white">{health?.success ? 'Online' : 'Indisponível'}</p>
              </div>
              <p className="mt-2 text-sm text-dark-400">{health?.message || 'Verificando...'}</p>
            </div>

            <div className="card">
              <div className="mb-3 flex items-center gap-2 text-dark-400">
                <Globe className="h-5 w-5" />
                <span className="text-sm">Domínio</span>
              </div>
              <p className="break-all text-lg font-semibold text-white">{APP_BASE_URL}</p>
              <p className="mt-2 text-sm text-dark-400">HTTPS ativo pelo Nginx.</p>
            </div>

            <div className="card">
              <div className="mb-3 flex items-center gap-2 text-dark-400">
                <Shield className="h-5 w-5" />
                <span className="text-sm">Segurança</span>
              </div>
              <p className="text-lg font-semibold text-white">JWT + token GPS</p>
              <p className="mt-2 text-sm text-dark-400">A porta Node fica privada em 127.0.0.1.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <section className="card">
              <div className="mb-5 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500/20 text-primary-400">
                  <Wifi className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Endpoint GPS</h2>
                  <p className="text-sm text-dark-400">Recebimento de posições dos rastreadores.</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">URL</label>
                  <div className="flex gap-2">
                    <input value={ingestUrl} readOnly className="input-field" />
                    <button
                      type="button"
                      onClick={() => copyText(ingestUrl, 'Endpoint')}
                      className="rounded-lg border border-dark-600 px-3 text-dark-300 transition-colors hover:bg-dark-700 hover:text-white"
                      title="Copiar endpoint"
                    >
                      <ClipboardCopy className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Header</label>
                  <div className="flex gap-2">
                    <input value="x-tracker-token: SEU_TOKEN_DO_RASTREADOR" readOnly className="input-field" />
                    <button
                      type="button"
                      onClick={() => copyText('x-tracker-token: SEU_TOKEN_DO_RASTREADOR', 'Header')}
                      className="rounded-lg border border-dark-600 px-3 text-dark-300 transition-colors hover:bg-dark-700 hover:text-white"
                      title="Copiar header"
                    >
                      <ClipboardCopy className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="rounded-lg border border-dark-700 bg-dark-900 p-4">
                  <div className="mb-2 flex items-center gap-2 text-sm font-medium text-dark-300">
                    <KeyRound className="h-4 w-4" />
                    Token do rastreador
                  </div>
                  <p className="text-sm text-dark-400">
                    O valor fica salvo somente no arquivo <span className="text-dark-200">backend/.env</span> do servidor.
                  </p>
                </div>
              </div>
            </section>

            <section className="card">
              <div className="mb-5 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-500/20 text-accent-400">
                  <Database className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Payload esperado</h2>
                  <p className="text-sm text-dark-400">Formato JSON aceito pela API.</p>
                </div>
              </div>

              <pre className="max-h-80 overflow-auto rounded-lg border border-dark-700 bg-dark-950 p-4 text-sm text-dark-200">
                {JSON.stringify(samplePayload, null, 2)}
              </pre>
            </section>
          </div>

          <section className="card mt-6">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-white">Teste rápido</h2>
                <p className="text-sm text-dark-400">Comando de exemplo para validar envio de posição.</p>
              </div>
              <button
                type="button"
                onClick={() => copyText(curlExample, 'Comando')}
                className="btn-secondary flex items-center gap-2"
              >
                <ClipboardCopy className="h-4 w-4" />
                Copiar
              </button>
            </div>

            <pre className="overflow-auto rounded-lg border border-dark-700 bg-dark-950 p-4 text-sm text-dark-200">
              {curlExample}
            </pre>
          </section>

          <section className="card mt-6">
            <div className="mb-5 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-success-500/20 text-success-400">
                <MapPin className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Simular posição pelo painel</h2>
                <p className="text-sm text-dark-400">
                  Envie uma posição autenticada para testar mapa, histórico e WebSocket.
                </p>
              </div>
            </div>

            <form onSubmit={handleSimulationSubmit} className="space-y-5">
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
                <div className="lg:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-dark-300">Veículo</label>
                  <select
                    name="vehicle_id"
                    value={simulation.vehicle_id}
                    onChange={handleSimulationChange}
                    className="input-field"
                  >
                    {vehicles.length === 0 ? (
                      <option value="">Nenhum veículo cadastrado</option>
                    ) : (
                      vehicles.map((vehicle) => (
                        <option key={vehicle.id} value={vehicle.id}>
                          {vehicle.name} - {vehicle.plate}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div className="lg:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-dark-300">IMEI</label>
                  <input
                    name="imei"
                    value={simulation.imei}
                    onChange={handleSimulationChange}
                    className="input-field"
                    placeholder="IMEI do rastreador"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Latitude</label>
                  <input
                    name="latitude"
                    value={simulation.latitude}
                    onChange={handleSimulationChange}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Longitude</label>
                  <input
                    name="longitude"
                    value={simulation.longitude}
                    onChange={handleSimulationChange}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Velocidade</label>
                  <input
                    type="number"
                    name="speed"
                    value={simulation.speed}
                    onChange={handleSimulationChange}
                    className="input-field"
                    min="0"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Bateria</label>
                  <input
                    type="number"
                    name="battery"
                    value={simulation.battery}
                    onChange={handleSimulationChange}
                    className="input-field"
                    min="0"
                    max="100"
                  />
                </div>

                <div className="lg:col-span-4">
                  <label className="mb-2 block text-sm font-medium text-dark-300">Endereço</label>
                  <input
                    name="address"
                    value={simulation.address}
                    onChange={handleSimulationChange}
                    className="input-field"
                    placeholder="Descrição do local"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-3 border-t border-dark-800 pt-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 text-sm text-dark-300">
                    <input
                      type="checkbox"
                      name="ignition"
                      checked={simulation.ignition}
                      onChange={handleSimulationChange}
                      className="h-4 w-4 accent-primary-500"
                    />
                    Ignição ligada
                  </label>
                  <label className="flex items-center gap-2 text-sm text-dark-300">
                    <input
                      type="checkbox"
                      name="motion"
                      checked={simulation.motion}
                      onChange={handleSimulationChange}
                      className="h-4 w-4 accent-primary-500"
                    />
                    Em movimento
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={simulationLoading || vehicles.length === 0}
                  className="btn-primary flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Send className="h-4 w-4" />
                  {simulationLoading ? 'Enviando...' : 'Enviar posição teste'}
                </button>
              </div>
            </form>
          </section>

          <section className="card mt-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-1 h-5 w-5 text-success-400" />
                <div>
                  <h3 className="font-semibold text-white">PM2</h3>
                  <p className="text-sm text-dark-400">Processo Node mantido em execução.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-1 h-5 w-5 text-success-400" />
                <div>
                  <h3 className="font-semibold text-white">Nginx</h3>
                  <p className="text-sm text-dark-400">Proxy HTTPS para a aplicação.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-1 h-5 w-5 text-success-400" />
                <div>
                  <h3 className="font-semibold text-white">Banco</h3>
                  <p className="text-sm text-dark-400">Conexão validada na inicialização.</p>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default Settings;
